import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { supabase } from '@/lib/supabase';

import type { MfaModalState } from '../components/MfaOverlay';
import type { LoginValues } from '../loginSchema';
import {
  authPasskey,
  isPasskeySupported,
  listMyPasskeys,
} from '../passkeys/api/passkeyService';

type Args = {
  form: UseFormReturn<LoginValues>;
  /** Recuerda el usuario y decide si ofrecer la entrada rápida. true = se abrió la oferta. */
  decidirOferta: (email: string) => Promise<boolean>;
  irAlDestino: () => void;
  /**
   * ¿Este navegador se sabe dueño de una passkey de ese email? Solo entonces el
   * segundo factor dispara la biometría solo. Si no, en Chrome (y en su PWA) el
   * diálogo abre con el QR del celular, que no sirve de nada acá.
   */
  navegadorTienePasskey: (email: string) => boolean;
  /** La biometría de este navegador funcionó: queda marcado como que la tiene. */
  alPasskeyOk: (email: string) => void;
};

/**
 * Ingreso con email y contraseña, con el segundo factor resuelto en línea
 * (biometría y, si hace falta, código TOTP) sin salir de /login.
 *
 * 🔴 `submitting` y `mfaModal` son los que sostienen el guard de LoginPage: si
 * el guard manda a la app antes de que termine el segundo factor,
 * RequireMFAVerified detecta aal1 y redirige a /auth/mfa-challenge, justo lo
 * que este flujo en línea evita.
 */
export function usePasswordLogin({
  form,
  decidirOferta,
  irAlDestino,
  navegadorTienePasskey,
  alPasskeyOk,
}: Args) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [mfaModal, setMfaModal] = useState<MfaModalState>({ kind: 'closed' });
  const [totpCode, setTotpCode] = useState('');
  const [totpSubmitting, setTotpSubmitting] = useState(false);

  /**
   * Fin de un ingreso completo. `submitting` se mantiene hasta decidir, para
   * que el guard no mande a la app antes que la oferta de activación.
   */
  async function terminar(email: string) {
    if (await decidirOferta(email)) {
      setSubmitting(false);
      setMfaModal({ kind: 'closed' });
      return;
    }
    irAlDestino();
  }

  async function onSubmit(values: LoginValues) {
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword(values);
    if (error) {
      setSubmitting(false);
      toast.error('No se pudo iniciar sesión', { description: error.message });
      return;
    }

    // Paralelo: AAL + factores TOTP + passkeys. Minimizamos awaits ANTES de
    // disparar authPasskey() para preservar la user activation del click.
    const [aalRes, factorsRes, passkeys] = await Promise.all([
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      supabase.auth.mfa.listFactors(),
      isPasskeySupported() ? listMyPasskeys().catch(() => []) : Promise.resolve([]),
    ]);
    const aal = aalRes.data;
    const needsMfa = aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2';

    if (!needsMfa) {
      toast.success('Bienvenido');
      await terminar(values.email);
      return;
    }

    const verifiedTotp = factorsRes.data?.totp?.find((f) => f.status === 'verified');
    const cuentaTienePasskey = (passkeys?.length ?? 0) > 0;
    // La biometría se dispara sola solo si este navegador tiene su passkey, o si
    // es el único segundo factor que hay. Sin eso, el TOTP va primero y la
    // biometría queda como botón: así Chrome no abre el QR del celular.
    const hasPasskey =
      cuentaTienePasskey && (navegadorTienePasskey(values.email) || !verifiedTotp);

    // Si hay passkey → modal "Activando…" + disparamos prompt biométrico
    // dentro del mismo handler (user activation viva).
    if (hasPasskey) {
      setMfaModal({ kind: 'bio' });
      try {
        await authPasskey();
      } catch (err) {
        const msg = (err as Error).message || '';
        const cancelled = msg.includes('NotAllowedError') || msg.includes('cancel');
        if (verifiedTotp) {
          setMfaModal({
            kind: 'totp',
            factorId: verifiedTotp.id,
            reason: cancelled ? 'bio-cancelled' : 'bio-failed',
          });
          setSubmitting(false);
          return;
        }
        // No TOTP fallback → mostramos error y cerramos sesión
        setSubmitting(false);
        setMfaModal({ kind: 'closed' });
        toast.error(cancelled ? 'Verificación cancelada' : 'Falló la biometría', {
          description: cancelled ? undefined : msg,
        });
        await supabase.auth.signOut();
        return;
      }
      // Todas las ramas del catch retornan: acá la biometría salió bien. Este
      // navegador tiene su passkey: se marca y no se ofrece activarla.
      toast.success('Bienvenido');
      alPasskeyOk(values.email);
      irAlDestino();
      return;
    }

    // Sin biometría automática → modal TOTP directo. Si la cuenta tiene
    // passkeys, queda el botón para probarla a mano (no se sabe si este
    // navegador la ve: Safari con iCloud sí, la PWA de Chrome casi nunca).
    if (verifiedTotp) {
      setMfaModal({
        kind: 'totp',
        factorId: verifiedTotp.id,
        reason: 'no-passkey',
        puedePasskey: cuentaTienePasskey && isPasskeySupported(),
      });
      setSubmitting(false);
      return;
    }

    // Sin passkey y sin TOTP enrolled pero MFA requerido → debe configurarlo
    setSubmitting(false);
    toast.error('2FA requerido', {
      description: 'Configurá tu segundo factor antes de acceder.',
    });
    navigate('/auth/mfa-challenge', { replace: true });
  }

  async function onTotpSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mfaModal.kind !== 'totp') return;
    if (totpCode.trim().length !== 6) {
      toast.error('Código incompleto', { description: '6 dígitos requeridos' });
      return;
    }
    setTotpSubmitting(true);
    try {
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
        factorId: mfaModal.factorId,
      });
      if (chErr || !ch) throw chErr ?? new Error('Challenge falló');
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: mfaModal.factorId,
        challengeId: ch.id,
        code: totpCode.trim(),
      });
      if (vErr) throw vErr;
      setTotpSubmitting(false);
      toast.success('2FA verificado');
      await terminar(form.getValues('email'));
    } catch (err) {
      setTotpSubmitting(false);
      toast.error('Código inválido', { description: (err as Error).message });
    }
  }

  /** El botón «Usar huella / Face ID» del diálogo de código: la biometría a pedido. */
  async function usarPasskey() {
    if (mfaModal.kind !== 'totp') return;
    const previo = mfaModal;
    setMfaModal({ kind: 'bio' });
    try {
      await authPasskey();
    } catch (err) {
      const msg = (err as Error).message || '';
      const cancelled = msg.includes('NotAllowedError') || msg.includes('cancel');
      // Vuelve al código, con el botón todavía a mano para reintentar.
      setMfaModal({ ...previo, reason: cancelled ? 'bio-cancelled' : 'bio-failed' });
      return;
    }
    toast.success('Bienvenido');
    alPasskeyOk(form.getValues('email'));
    irAlDestino();
  }

  async function onCancelMfa() {
    setMfaModal({ kind: 'closed' });
    setTotpCode('');
    await supabase.auth.signOut();
  }

  return {
    submitting,
    mfaModal,
    totpCode,
    setTotpCode,
    totpSubmitting,
    onSubmit,
    onTotpSubmit,
    onCancelMfa,
    usarPasskey,
  };
}
